import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { cloneDeep, get, set } from "lodash";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import { Divider } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BooleanSetting from "../../atoms/BooleanSetting";
import InputSetting from "../../atoms/InputSetting";
import SingleSelectDropdown from "../../atoms/SingleSelectDropdown";
import MultiSelectDropdown from "../../atoms/MultiSelectDropdown";

const componentTypeMap = {
  boolean: {
    Component: BooleanSetting,
  },
  number: {
    Component: InputSetting,
  },
  "single-select-dropdown": {
    Component: SingleSelectDropdown,
  },
  "multi-select-dropdown": {
    Component: MultiSelectDropdown,
  },
};

const NestedMetrics = ({
  uniqueKey,
  nestedMetricsRow,
  metricEntry,
  nestedRow,
  notification,
  setting,
  index,
  validations,
  setParentState,
  clearErrors,
  metricsPath,
  stateUpdatePath,
}) => {
  // Check nested metrics is array or not
  const isNestedArrayMatrix = Array.isArray(nestedRow.metrics);

  // if array metrics then set empty array or else set Object entries
  let nestedMetricEntries = isNestedArrayMatrix ? [] : Object.entries(nestedRow.metrics);

  const nestedMetricsValidations = validations?.metrics?.[metricEntry[0]]?.metrics;

  const deepNestedMetrics = nestedMetricEntries.length
    ? nestedMetricEntries?.some(
        (nestedMetricEntry) =>
          nestedMetricsRow?.metrics[nestedMetricEntry[0]].type === "nested-metrics"
      )
    : false;

  const MetricsTitle = () =>
    useMemo(
      () =>
        "active" in nestedRow ? (
          <Grid container spacing={6}>
            <Grid item xs={1}>
              <BooleanSetting
                setting={{
                  field_value: nestedRow.active,
                }}
                setParentState={(checked) => {
                  const copy = cloneDeep(setting.field_value);
                  set(copy, [...stateUpdatePath, "active"], checked);
                  setParentState(copy);
                  clearErrors();
                }}
                styles={{
                  marginBottom: "1rem",
                }}
              />
            </Grid>
            <Grid item xs={11} container alignItems="center" justifyContent="flex-start">
              <Typography variant="p">{nestedMetricsRow?.description}</Typography>
            </Grid>
          </Grid>
        ) : null,
      [
        uniqueKey,
        nestedMetricsRow,
        metricEntry,
        nestedRow,
        notification,
        setting,
        index,
        validations,
        metricsPath,
        stateUpdatePath,
      ]
    );

  const MetricsBody = () =>
    useMemo(
      () =>
        isNestedArrayMatrix ? (
          <Box style={{ width: "100%", marginBottom: "1.5rem" }}>
            {nestedRow.metrics.map((metric, i) => {
              nestedMetricEntries = Object.entries(metric);
              return (
                <Grid
                  // eslint-disable-next-line react/no-array-index-key
                  key={`nested-metric-${uniqueKey}-${i}`}
                  justifyContent="space-between"
                  sx={{
                    width: "100%",
                  }}
                >
                  {nestedMetricEntries
                    .sort((metricEntryA, metricEntryB) => {
                      const metricsArray = get(setting.field_value, metricsPath);
                      const matchedMetricsA = Array.isArray(metricsArray)
                        ? metricsArray?.find((matrix) => matrix[metricEntryA[0]])
                        : metricsArray;
                      const matchedMetricsB = Array.isArray(metricsArray)
                        ? metricsArray?.find((matrix) => matrix[metricEntryB[0]])
                        : metricsArray;
                      const orderOfA =
                        matchedMetricsA[metricEntryA[0]]?.order ||
                        matchedMetricsA[metricEntryA[0]].length;
                      const orderOfB =
                        matchedMetricsB[metricEntryB[0]]?.order ||
                        matchedMetricsB[metricEntryB[0]].length;
                      if (orderOfA === orderOfB) return 0;
                      if (orderOfA < orderOfB) {
                        return -1;
                      }
                      return 1;
                    })
                    .map((nestedMetricEntry, nestedMetricEntryIndex) => {
                      let nestedMetricEntryValidations = {};

                      if ("active" in nestedRow && !nestedRow.active) {
                        nestedMetricEntryValidations = {
                          required: false,
                          validate: () => true,
                        };
                      } else {
                        nestedMetricEntryValidations = nestedMetricsValidations?.[
                          nestedMetricEntry[0]
                        ] || { required: false };

                        if (nestedMetricEntryValidations?.length) {
                          nestedMetricEntryValidations.validate = (val) => {
                            if (!val || val?.length < nestedMetricEntryValidations.length)
                              return (
                                nestedMetricEntryValidations.message ||
                                "Please select at least 1 option"
                              );

                            return true;
                          };
                          delete nestedMetricEntryValidations.message;
                        }
                      }

                      const metricsArray = get(setting.field_value, metricsPath);
                      const matchedMetrics = Array.isArray(metricsArray)
                        ? metricsArray?.find((matrix) => matrix[nestedMetricEntry[0]])
                        : metricsArray;
                      const nestedComponentTypeKey = matchedMetrics[nestedMetricEntry[0]].type;
                      const NestedMetricComponent =
                        componentTypeMap[nestedComponentTypeKey].Component;

                      const options =
                        // eslint-disable-next-line no-nested-ternary
                        nestedMetricEntry[0] === "recipientList"
                          ? notification.recipientListValues[
                              get(
                                setting.field_value,
                                [...stateUpdatePath, "metrics", i, "recipientType"],
                                ""
                              )
                            ]
                          : nestedMetricEntry[0] === "recipientType"
                          ? notification.notificationRecipientTypeValues
                          : nestedMetricsRow.metrics[0][nestedMetricEntry[0]]?.picklistValues || [];

                      const formattedFieldValue =
                        // eslint-disable-next-line no-nested-ternary
                        nestedComponentTypeKey === "single-select-dropdown"
                          ? nestedMetricEntry[1]
                            ? {
                                value: nestedMetricEntry[1],
                                display:
                                  nestedMetricEntry[0] === "team"
                                    ? nestedMetricsRow.metrics[0][
                                        nestedMetricEntry[0]
                                      ]?.picklistValues?.find(
                                        (team) => team.value === nestedMetricEntry[1]
                                      )?.display
                                    : nestedMetricEntry[1],
                              }
                            : null
                          : nestedComponentTypeKey === "multi-select-dropdown"
                          ? nestedMetricEntry[1]?.map((entry) => ({
                              value: entry,
                              display:
                                nestedMetricEntry[0] === "recipientList"
                                  ? options.find((option) => option.value === entry)?.display ||
                                    entry
                                  : entry,
                            })) || nestedMetricEntry[1]
                          : nestedMetricEntry[1];
                      return (
                        <Grid
                          container
                          // eslint-disable-next-line react/no-array-index-key
                          key={`nested-metric-${uniqueKey}-${nestedMetricEntry[0]}-${i}-${nestedMetricEntryIndex}`}
                        >
                          <Grid
                            item
                            xs={
                              nestedMetricEntryIndex === 0 &&
                              get(setting.field_value, [...stateUpdatePath, "metrics"], [])
                                ?.length > 1
                                ? 11
                                : 12
                            }
                          >
                            <NestedMetricComponent
                              name={`nested-metric-${uniqueKey}-${nestedMetricEntry[0]}-${i}-${nestedMetricEntryIndex}`}
                              setting={{
                                field_value: formattedFieldValue,
                                en_field_label:
                                  matchedMetrics[nestedMetricEntry[0]]?.description ||
                                  nestedMetricEntry[0],
                                options,
                              }}
                              setParentState={(newSelectedValue) => {
                                const copy = cloneDeep(setting.field_value);

                                set(
                                  copy,
                                  [...stateUpdatePath, "metrics", i, nestedMetricEntry[0]],
                                  Array.isArray(newSelectedValue)
                                    ? newSelectedValue.map((value) => value?.value || value)
                                    : newSelectedValue?.value || newSelectedValue || ""
                                );

                                if (nestedMetricEntry[0] === "recipientType") {
                                  set(
                                    copy,
                                    [...stateUpdatePath, "metrics", i, "recipientList"],
                                    []
                                  );
                                }
                                setParentState(copy);
                              }}
                              styles={{
                                width: "100%",
                                marginBottom: "1.5rem",
                              }}
                              validations={nestedMetricEntryValidations}
                            />
                          </Grid>
                          {nestedMetricEntryIndex === 0 &&
                          get(setting.field_value, [...stateUpdatePath, "metrics"], [])?.length >
                            1 ? (
                            <Grid item xs={1}>
                              <IconButton
                                size="large"
                                color="inherit"
                                variant="contained"
                                onClick={() => {
                                  const copy = cloneDeep(setting.field_value);

                                  const matchedArray = get(
                                    copy,
                                    [...stateUpdatePath, "metrics"],
                                    []
                                  );
                                  matchedArray.splice(i, 1);
                                  set(copy, [...stateUpdatePath, "metrics"], matchedArray);

                                  setParentState(copy);
                                  clearErrors();
                                }}
                              >
                                <Icon fontSize="medium">delete</Icon>
                              </IconButton>
                            </Grid>
                          ) : null}
                        </Grid>
                      );
                    })}
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </Grid>
              );
            })}
            <MDButton
              my={2}
              onClick={() => {
                const copy = cloneDeep(setting.field_value);

                const matchedArray = get(copy, [...stateUpdatePath, "metrics"], []);
                matchedArray.push(
                  Object.keys(get(copy, metricsPath)[0]).reduce((result, resultKey) => {
                    result[resultKey] =
                      // eslint-disable-next-line no-nested-ternary
                      resultKey === "recipientList"
                        ? []
                        : resultKey === "recipientType"
                        ? "Channel"
                        : "";
                    return result;
                  }, {})
                );

                set(copy, [...stateUpdatePath, "metrics"], matchedArray);

                setParentState([...copy]);
              }}
              variant="gradient"
              color="newColor"
            >
              Add Team
            </MDButton>
          </Box>
        ) : (
          !!nestedMetricEntries.length && (
            <Grid
              container
              justifyContent="space-between"
              sx={{
                width: "100%",
                marginBottom: "1.5rem",
              }}
            >
              {nestedMetricEntries
                .sort((metricEntryA, metricEntryB) => {
                  const orderOfA =
                    nestedMetricsRow?.metrics[metricEntryA[0]]?.order ||
                    nestedMetricsRow?.metrics[metricEntryA[0]].length;
                  const orderOfB =
                    nestedMetricsRow?.metrics[metricEntryB[0]]?.order ||
                    nestedMetricsRow?.metrics[metricEntryB[0]].length;
                  if (orderOfA === orderOfB) return 0;
                  if (orderOfA < orderOfB) {
                    return -1;
                  }
                  return 1;
                })
                .map((nestedMetricEntry) => {
                  const deepNestedRow = nestedMetricEntry[1];
                  const deepNestedMetricsRow = nestedMetricsRow?.metrics[nestedMetricEntry[0]];
                  const nestedComponentTypeKey = deepNestedMetricsRow.type;

                  if (nestedComponentTypeKey === "nested-metrics") {
                    return (
                      <NestedMetrics
                        key={`${uniqueKey}-${nestedMetricEntry[0]}`}
                        uniqueKey={`${uniqueKey}-${nestedMetricEntry[0]}`}
                        nestedMetricsRow={deepNestedMetricsRow}
                        nestedRow={deepNestedRow}
                        metricEntry={nestedMetricEntry}
                        notification={notification}
                        setting={setting}
                        index={index}
                        validations={validations?.metrics?.[metricEntry[0]]}
                        setParentState={setParentState}
                        clearErrors={clearErrors}
                        metricsPath={[...metricsPath, nestedMetricEntry[0], "metrics"]}
                        stateUpdatePath={[...stateUpdatePath, "metrics", nestedMetricEntry[0]]}
                      />
                    );
                  }

                  const NestedMetricComponent = componentTypeMap[nestedComponentTypeKey].Component;
                  const options =
                    // eslint-disable-next-line no-nested-ternary
                    nestedMetricEntry[0] === "recipientList"
                      ? notification.recipientListValues[
                          get(
                            setting.field_value,
                            [...stateUpdatePath, "metrics", "recipientType"],
                            []
                          )
                        ]
                      : nestedMetricEntry[0] === "recipientType"
                      ? notification.notificationRecipientTypeValues
                      : nestedMetricsRow.metrics[0][nestedMetricEntry[0]]?.picklistValues || [];

                  const formattedFieldValue =
                    // eslint-disable-next-line no-nested-ternary
                    nestedComponentTypeKey === "single-select-dropdown"
                      ? {
                          value: nestedMetricEntry[1],
                          display: nestedMetricEntry[1],
                        }
                      : nestedComponentTypeKey === "multi-select-dropdown"
                      ? nestedMetricEntry[1]?.map((entry) => ({
                          value: entry,
                          display:
                            nestedMetricEntry[0] === "recipientList"
                              ? options.find((option) => option.value === entry)?.display || entry
                              : entry,
                        })) || nestedMetricEntry[1]
                      : nestedMetricEntry[1];

                  let nestedMetricEntryValidations = {};

                  if ("active" in nestedRow && !nestedRow.active) {
                    nestedMetricEntryValidations = {
                      required: false,
                      validate: () => true,
                    };
                  } else {
                    nestedMetricEntryValidations = nestedMetricsValidations?.[
                      nestedMetricEntry[0]
                    ] || { required: false };

                    if (nestedMetricEntryValidations?.length) {
                      nestedMetricEntryValidations.validate = (val) => {
                        if (!val || val?.length < nestedMetricEntryValidations.length)
                          return (
                            nestedMetricEntryValidations.message ||
                            "Please select at least 1 option"
                          );

                        return true;
                      };
                      delete nestedMetricEntryValidations.message;
                    }
                  }

                  return (
                    <Grid
                      key={`nested-metric-entries-${uniqueKey}-${nestedMetricEntry?.[0]}`}
                      item
                      xs={12}
                    >
                      <NestedMetricComponent
                        name={`nested-metric-entries-${uniqueKey}-${nestedMetricEntry?.[0]}`}
                        setting={{
                          field_value: formattedFieldValue,
                          en_field_label:
                            nestedMetricsRow?.metrics[nestedMetricEntry[0]]?.description ||
                            nestedMetricEntry[0],
                          options,
                        }}
                        setParentState={(newSelectedValue) => {
                          const copy = cloneDeep(setting.field_value);

                          set(
                            copy,
                            [...stateUpdatePath, "metrics", nestedMetricEntry[0]],
                            Array.isArray(newSelectedValue)
                              ? newSelectedValue.map((value) => value?.value || value)
                              : newSelectedValue?.value || newSelectedValue
                          );

                          if (nestedMetricEntry[0] === "recipientType") {
                            set(copy, [...stateUpdatePath, "metrics", "recipientList"], []);
                          }
                          setParentState(copy);
                        }}
                        validations={nestedMetricEntryValidations}
                        styles={{
                          width: "100%",
                          marginBottom: "1.5rem",
                        }}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          )
        ),
      [
        uniqueKey,
        nestedMetricsRow,
        metricEntry,
        nestedRow,
        notification,
        setting,
        index,
        validations,
        metricsPath,
        stateUpdatePath,
      ]
    );

  if (deepNestedMetrics) {
    return (
      <Accordion
        sx={{
          width: "100%",
        }}
        key={`accordion-${uniqueKey}-${notification?.description}`}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          {"active" in nestedRow ? <MetricsTitle /> : null}
        </AccordionSummary>
        <AccordionDetails>
          <MetricsBody />
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Grid container key={`nested-metrics-container-${uniqueKey}`}>
      {"active" in nestedRow ? <MetricsTitle /> : null}
      <MetricsBody />
    </Grid>
  );
};

export default NestedMetrics;
